import { Box, Button, MenuItem, TextField } from "@mui/material"
import {
  get_number_geojson_properties,
  spatial_merge,
} from "../../utils/layer_actions"
import axios from "axios"
import { useState } from "react"
import { useEffect } from "react"
import { toast } from "react-toastify"
import HandleFail from "../../utils/error_handler"
import feature_schema from "../../configs/feature_schema.json"

const allocation_properties = [
  "population",
  "male",
  "female",
  "insured",
  "uninsured",
]

const run_allocation = async ({
  facility_types,
  au_geo_json,
  fac_geo_json,
  au_pop_property,
  distance_constraint,
  dispatch_map_data,
  dispatch_ui,
}) => {
  dispatch_ui({ type: "START_LOADING" })
  await HandleFail(async () => {
    if (!au_geo_json) {
      throw new Error("No Analysis Units Imported. Can not run the allocation.")
    }
    if (!fac_geo_json) {
      throw new Error("No Facilities imported. Can not run the allocation.")
    }
    if (!au_pop_property) {
      throw new Error(
        "No Allocation Property Selected. Can not run the allocation.",
      )
    }
    if (facility_types.length === 0) {
      throw new Error("No facility types selected.")
    }

    let filtered_geo_json = {
      type: "FeatureCollection",
      features: fac_geo_json.features.filter((feature) =>
        facility_types.includes(feature.properties.type),
      ),
    }

    filtered_geo_json = spatial_merge(filtered_geo_json, au_geo_json)

    const allocation_result = await allocation_request({
      au_geo_json,
      fac_geo_json: filtered_geo_json,
      urban_distance: distance_constraint,
      rural_distance: distance_constraint,
      au_pop_property,
      allow_partial_allocations: true,
      run_second_pass: false,
    })
    const { analysis_units, facilities } = allocation_result

    dispatch_map_data({
      type: "APPLY_ALLOCATION",
      payload: {
        facilities_results: facilities,
        analysis_units_results: analysis_units,
      },
    })

    toast.success("Allocation ran successfully")
  })
  dispatch_ui({ type: "STOP_LOADING" })
}

const allocation_request = async (allocation_data) => {
  const {
    au_geo_json,
    fac_geo_json,
    urban_distance,
    rural_distance,
    au_pop_property,
    allow_partial_allocations,
    run_second_pass,
  } = allocation_data

  let facilities = {}

  fac_geo_json.features.forEach((feature) => {
    facilities[feature.id] = {
      au_id: feature.properties[feature_schema.facility.au_id_property],
      capacity: 10000000,
    }
  })

  let analysis_units = {}
  au_geo_json.features.forEach((feature) => {
    analysis_units[feature.properties[feature_schema.au.id_property]] = {
      demand: feature.properties[au_pop_property],
      urban_core: feature.properties[feature_schema.au.core_property],
    }
  })

  const allocation_parameters = {
    max_core_distance: urban_distance,
    max_remote_distance: rural_distance,
    facilities,
    analysis_units,
    allow_partial_allocations,
    run_second_pass,
  }

  const response = await axios.post("/api/allocation", allocation_parameters)

  return response.data
}

const Allocate = ({ map_data, dispatch_map_data, dispatch_ui }) => {
  const [distance_constraint, set_distance_constraint] = useState(20000)
  const [au_pop_property, set_au_pop_property] = useState("population")
  const [facility_types, set_facility_types] = useState([
    "district",
    "regional",
    "private_acute",
  ])

  const { au_geo_json, fac_geo_json } = map_data

  useEffect(() => {
    if (!au_pop_property && au_geo_json) {
      const options = get_number_geojson_properties(au_geo_json)
      if (options.includes("population")) {
        set_au_pop_property("population")
      } else {
        set_au_pop_property(options[0])
      }
    }
  }, [au_pop_property, au_geo_json, set_au_pop_property])

  if (!fac_geo_json) {
    return null
  }

  // GP should not be an option
  const fac_options = Array.from(
    new Set(
      fac_geo_json.features
        .filter((feature) => feature.properties.type !== "gp")
        .map((feature) => feature.properties.type),
    ),
  )

  return (
    <Box sx={{ width: "100%", marginTop: 2 }}>
      <TextField
        label="Distance Constraint (meters)"
        fullWidth
        type="number"
        variant="standard"
        value={distance_constraint}
        onChange={(event) => {
          const input_value = event.target.value
          if (input_value >= 0) {
            set_distance_constraint(input_value)
          }
        }}
      />

      <TextField
        fullWidth
        select
        label="Population Type"
        type="text"
        variant="standard"
        value={au_pop_property}
        onChange={(event) => {
          const input_value = event.target.value
          set_au_pop_property(input_value)
        }}
      >
        {allocation_properties.sort().map((type) => {
          return (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          )
        })}
      </TextField>

      <TextField
        fullWidth
        select
        SelectProps={{
          multiple: true,
          value: facility_types,
          onChange: (event) => {
            set_facility_types(event.target.value)
          },
        }}
        label="Facility Types"
        type="text"
        variant="standard"
      >
        {fac_options.sort().map((type) => {
          return (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          )
        })}
      </TextField>

      <Button
        sx={{ marginTop: 1 }}
        variant="outlined"
        fullWidth
        onClick={() =>
          run_allocation({
            facility_types,
            au_geo_json,
            fac_geo_json,
            au_pop_property,
            distance_constraint,
            dispatch_map_data,
            dispatch_ui,
          })
        }
      >
        Run Allocation
      </Button>
    </Box>
  )
}

export default Allocate
